import { type ComponentPropsWithoutRef, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsDownUp, FolderPlus, ImagePlus, type LucideIcon, Search, X } from 'lucide-react';
import {
  ModalId,
  useBackModal,
  useCurrentModalId,
  useModalStack,
  useOpenModal,
} from '@/hooks/data/modal/useModalStore';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TREE_SEARCH_SCOPE, useTreeSearch } from '@/hooks/data/tree/useTreeSearch';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { TreeSearch } from '@/components/sidebar/tree/tree-search';
import { getFolderWithAncestors } from '@/services/folder.service';
import { Tree, TreeMode } from '@/components/sidebar/tree/tree';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { Modal, ModalContent } from '@/components/ui/modal';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTreeContext } from '@/context/TreeContext';
import { useTree } from '@/hooks/data/tree/useTree';
import { error as errorLog } from '@/utilities/log';
import { Button } from '@/components/ui/button';
import { DAMNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type ToolbarActionProps = {
  icon: LucideIcon;
  iconClassName?: string;
  tooltipDelay?: number;
} & Omit<ComponentPropsWithoutRef<typeof Button>, 'children'>;

export const ToolbarAction = forwardRef<HTMLButtonElement, ToolbarActionProps>(
  ({ icon: Icon, tooltipDelay, className, iconClassName, disabled, ...buttonProps }, ref) => (
    <Button
      ref={disabled ? undefined : ref}
      variant="main-toolbar"
      size="main-toolbar"
      className={cn('p-0 icon-6', className)}
      disabled={disabled}
      {...buttonProps}
      icon={Icon}
      iconClassName={cn('stroke-icon-base icon-6', iconClassName)}
    />
  ),
);

ToolbarAction.displayName = 'ToolbarAction';

export const UploadsTreeSelectorDialog = () => {
  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const tTree = useTranslations('tree');
  const openModal = useOpenModal();
  const backModal = useBackModal();
  const current = useCurrentModalId();
  const modalSequence = useModalStack();
  const isOpen = current === ModalId.IngestionFolderSelect || current === ModalId.IngestionAlbumSelect;
  const activeId = isOpen ? current : ModalId.IngestionFolderSelect;
  const mode = current;

  const { tree } = useTree();
  const { reusableTreeOpenIds, setReusableTreeOpenIds } = useTreeContext();

  const reusableTreeSelection = useTreeStore((state) => state.reusableTreeSelection);
  const setReusableTreeSelection = useTreeStore((state) => state.setReusableTreeSelection);
  const setReusableTreeSelectedFolder = useTreeStore((state) => state.setReusableTreeSelectedFolder);
  const reusableTreeSelectedFolder = useTreeStore((state) => state.reusableTreeSelectedFolder);
  const findNodePath = useTreeStore((state) => state.findNodePath);
  const folderAncestorsFn = useAuthenticatedQueryFn(getFolderWithAncestors);
  const changeFolder = useUploadStore((state) => state.changeFolder);
  const setUploadFolderPath = useUploadStore((state) => state.setUploadFolderPath);
  const setSelectedAlbums = useUploadStore((state) => state.setSelectedAlbums);
  const getActiveUploadSession = useUploadStore((state) => state.getActiveUploadSession);
  const {
    session: { selectedAlbums, uploadFolderPath },
    uploadSessionId,
  } = getActiveUploadSession();

  const searchType =
    mode === ModalId.IngestionAlbumSelect
      ? DAMNodeType.Album
      : mode === ModalId.IngestionFolderSelect
        ? DAMNodeType.Folder
        : undefined;

  const {
    searchTerm: ingestionSearchInput,
    setSearchTerm: setIngestionSearchInput,
    committedTerm: ingestionSearchQuery,
    results: ingestionSearchResults,
    isFetching: isIngestionSearchLoading,
    isActive: isIngestionSearchActive,
    clearSearch: clearIngestionSearch,
  } = useTreeSearch({ type: searchType, scope: TREE_SEARCH_SCOPE.INGESTION });

  const alreadySelectedAlbum = selectedAlbums?.find((album) => album.id === reusableTreeSelection?.id);

  const uploadPathSegments = useMemo(
    () => (uploadFolderPath ? uploadFolderPath.split('/').filter(Boolean) : []),
    [uploadFolderPath],
  );
  const searchPlaceholder =
    searchType === DAMNodeType.Album
      ? 'Search albums'
      : searchType === DAMNodeType.Folder
        ? 'Search folders'
        : 'Search tree';

  const [pendingScrollId, setPendingScrollId] = useState<string | undefined>(undefined);
  const handleTreeScroll = useCallback((scrolledId: string) => {
    setPendingScrollId((prev) => (prev === scrolledId ? undefined : prev));
  }, []);

  const hasCapturedFolderSelectionRef = useRef(false);
  const [isResolvingSelection, setIsResolvingSelection] = useState(false);

  const resolveFolderPath = useCallback(
    async (folderId: string, fallbackLabel?: string) => {
      const namePath = findNodePath(folderId, 'name');
      if (namePath?.length) {
        return `/${namePath.join('/')}`;
      }

      try {
        const folderDetails = await folderAncestorsFn({ folderId });
        const orderedAncestors = [...(folderDetails.ancestors ?? [])].reverse();
        const ancestorNames =
          orderedAncestors.map((ancestor) => ancestor.name).filter((value): value is string => Boolean(value)) ?? [];
        const leafName = folderDetails.name ?? fallbackLabel;
        const pathParts = [...ancestorNames, leafName].filter((value): value is string => Boolean(value));

        return pathParts.length ? `/${pathParts.join('/')}` : undefined;
      } catch (error) {
        errorLog('Action failed:', error);
        return undefined;
      }
    },
    [findNodePath, folderAncestorsFn],
  );

  const handleCancel = useCallback(() => {
    setReusableTreeSelection(undefined);
    backModal();
  }, [setReusableTreeSelection, backModal]);

  const toolbarActionDisabled = isIngestionSearchActive || isIngestionSearchLoading;
  const continueDisabled =
    isResolvingSelection ||
    (mode === ModalId.IngestionFolderSelect
      ? reusableTreeSelection?.type !== DAMNodeType.Folder
      : mode === ModalId.IngestionAlbumSelect
        ? reusableTreeSelection?.type !== DAMNodeType.Album || Boolean(alreadySelectedAlbum)
        : true);

  useEffect(() => {
    if (!isOpen || mode !== ModalId.IngestionFolderSelect) {
      return;
    }

    setReusableTreeOpenIds({});
  }, [isOpen, mode, setReusableTreeOpenIds]);

  useEffect(() => {
    const isIngestionInSequence = modalSequence.some(
      ({ id }) => id === ModalId.IngestionFolderSelect || id === ModalId.IngestionAlbumSelect,
    );

    if (!isIngestionInSequence) {
      clearIngestionSearch();
    }
  }, [modalSequence, clearIngestionSearch]);

  useEffect(() => {
    if (!isOpen || mode !== ModalId.IngestionFolderSelect) {
      hasCapturedFolderSelectionRef.current = false;
      return;
    }

    if (hasCapturedFolderSelectionRef.current) {
      return;
    }

    hasCapturedFolderSelectionRef.current = true;

    if (!reusableTreeSelectedFolder) {
      return;
    }

    const namePath = findNodePath(reusableTreeSelectedFolder, 'name');
    const fallbackLabel = uploadPathSegments.length > 0 ? uploadPathSegments[uploadPathSegments.length - 1] : '';
    const label = namePath?.length ? (namePath[namePath.length - 1] ?? fallbackLabel) : fallbackLabel;
    setReusableTreeSelection({
      id: reusableTreeSelectedFolder,
      type: DAMNodeType.Folder,
      label,
    });

    const fallbackIdPath = findNodePath(reusableTreeSelectedFolder, 'id') ?? [];
    const idsToOpen = fallbackIdPath.length > 1 ? fallbackIdPath.slice(0, -1) : [];

    if (idsToOpen.length > 0) {
      setReusableTreeOpenIds((prev) => {
        const newOpenIds = { ...prev };
        let changed = false;

        idsToOpen.forEach((id: string) => {
          if (!id) {
            return;
          }

          if (!newOpenIds[id]) {
            newOpenIds[id] = true;
            changed = true;
          }
        });

        return changed ? newOpenIds : prev;
      });
    }
  }, [
    isOpen,
    mode,
    reusableTreeSelectedFolder,
    uploadFolderPath,
    uploadPathSegments,
    findNodePath,
    setReusableTreeSelection,
    setReusableTreeOpenIds,
  ]);

  const handleContinue = useCallback(async () => {
    if (mode === ModalId.IngestionFolderSelect && reusableTreeSelection?.type === DAMNodeType.Folder) {
      setIsResolvingSelection(true);
      try {
        const resolvedPath = await resolveFolderPath(reusableTreeSelection.id, reusableTreeSelection.label);

        if (!resolvedPath) {
          return;
        }

        if (resolvedPath !== uploadFolderPath) {
          changeFolder(uploadSessionId, uploadFolderPath, resolvedPath);
          setUploadFolderPath(uploadSessionId, resolvedPath);
        }

        setReusableTreeSelectedFolder(reusableTreeSelection.id);
        setReusableTreeSelection(undefined);
        backModal();
      } finally {
        setIsResolvingSelection(false);
      }
      return;
    }

    if (mode === ModalId.IngestionAlbumSelect && reusableTreeSelection && !alreadySelectedAlbum) {
      setSelectedAlbums(uploadSessionId, [
        ...selectedAlbums,
        { id: reusableTreeSelection.id, label: reusableTreeSelection.label },
      ]);
    }

    setReusableTreeSelection(undefined);
    backModal();
  }, [
    mode,
    reusableTreeSelection,
    resolveFolderPath,
    uploadFolderPath,
    changeFolder,
    setUploadFolderPath,
    uploadSessionId,
    setReusableTreeSelectedFolder,
    alreadySelectedAlbum,
    selectedAlbums,
    setSelectedAlbums,
    backModal,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setPendingScrollId(undefined);

      if (!reusableTreeSelectedFolder) {
        setReusableTreeOpenIds((prev) => (Object.keys(prev).length > 0 ? {} : prev));
      }
      return;
    }

    if (mode === ModalId.IngestionFolderSelect && !reusableTreeSelectedFolder) {
      setPendingScrollId(undefined);
      setReusableTreeOpenIds((prev) => (Object.keys(prev).length > 0 ? {} : prev));
    }
  }, [isOpen, mode, reusableTreeSelectedFolder, setReusableTreeOpenIds]);

  useEffect(() => {
    if (!isOpen || mode !== ModalId.IngestionFolderSelect || !reusableTreeSelectedFolder) {
      return;
    }

    setPendingScrollId((prev) => (prev === reusableTreeSelectedFolder ? prev : reusableTreeSelectedFolder));
  }, [isOpen, mode, reusableTreeSelectedFolder]);

  useEffect(() => {
    if (mode !== ModalId.IngestionFolderSelect) {
      setPendingScrollId(undefined);
    }
  }, [mode]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal id={activeId} backOnClose>
      <ModalContent onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {mode === ModalId.IngestionFolderSelect
              ? tDialogs('selectUploadDestinationTitle')
              : tDialogs('selectAlbumTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex h-12 items-center justify-between gap-x-3 border-t border-t-toolbar-bevel bg-ui-base px-3 py-1.5">
          <TreeSearch
            value={ingestionSearchInput}
            onValueChange={setIngestionSearchInput}
            onClear={clearIngestionSearch}
            isLoading={isIngestionSearchLoading}
            placeholder={searchPlaceholder}
            toggleElement={
              <ToolbarAction
                icon={Search}
                aria-label={tDialogs('searchFolderOrAlbumTooltip')}
                tooltip={
                  mode === ModalId.IngestionFolderSelect
                    ? tDialogs('searchFolderTooltip')
                    : tDialogs('searchAlbumTooltip')
                }
              />
            }
          />
          <div className="flex items-center gap-x-2">
            <ToolbarAction
              icon={FolderPlus}
              aria-label={tDialogs('createNewFolder')}
              disabled={reusableTreeSelection?.type !== DAMNodeType.Folder || toolbarActionDisabled}
              {...(reusableTreeSelection?.type === DAMNodeType.Folder &&
                reusableTreeSelection.id && {
                  onClick: () =>
                    openModal(ModalId.CreateFolder, {
                      id: reusableTreeSelection?.id,
                    }),
                })}
              tooltip={
                reusableTreeSelection?.type === DAMNodeType.Folder
                  ? tDialogs('newFolderTooltip')
                  : tDialogs('selectFolderFirstTooltip')
              }
            />
            {mode !== ModalId.IngestionFolderSelect && (
              <ToolbarAction
                icon={ImagePlus}
                aria-label={tDialogs('createNewAlbum')}
                disabled={reusableTreeSelection?.type !== DAMNodeType.Folder || toolbarActionDisabled}
                {...(reusableTreeSelection?.type === DAMNodeType.Folder &&
                  reusableTreeSelection.id && {
                    onClick: () =>
                      openModal(ModalId.CreateAlbum, {
                        id: reusableTreeSelection?.id,
                      }),
                  })}
                tooltip={
                  reusableTreeSelection?.type === DAMNodeType.Folder
                    ? tDialogs('newAlbumTooltip')
                    : tDialogs('selectFolderFirstTooltip')
                }
              />
            )}
            <ToolbarAction
              icon={ChevronsDownUp}
              aria-label={tTree('toggleCollapse')}
              onClick={() => setReusableTreeOpenIds({})}
              disabled={toolbarActionDisabled || Object.keys(reusableTreeOpenIds).length === 0}
              tooltip={tCommon('collapse')}
            />
          </div>
        </div>
        <DialogDescription>
          <ScrollArea className="max-h-[calc(100vh-15rem)] min-h-40 overflow-y-auto bg-ui-panel">
            <Tree
              setOpenIds={setReusableTreeOpenIds}
              openIds={reusableTreeOpenIds}
              tree={tree}
              className={cn('mb-2 h-full grow', {
                'mt-2': isIngestionSearchActive,
              })}
              mode={mode === ModalId.IngestionFolderSelect ? TreeMode.SelectFolder : TreeMode.SelectAlbum}
              scrollToNodeId={pendingScrollId}
              onScrollToNode={handleTreeScroll}
              searchResults={ingestionSearchResults}
              isSearching={isIngestionSearchActive}
              isSearchLoading={isIngestionSearchLoading}
              searchQuery={ingestionSearchQuery}
              disableDrag
            />
          </ScrollArea>
        </DialogDescription>
        <DialogFooter>
          <Button
            // disabled={isLoading}
            variant="secondary"
            data-cy="cancel-upload-folder-button"
            onClick={handleCancel}
            icon={X}
          >
            {tCommon('cancel')}
          </Button>
          <Button
            disabled={continueDisabled}
            variant="info"
            data-cy="continue-upload-folder-button"
            onClick={handleContinue}
            icon={Check}
          >
            {tCommon('continue')}
          </Button>
        </DialogFooter>
      </ModalContent>
    </Modal>
  );
};
